
type SyntheticChangeEvent = ChangeEvent<HTMLInputElement> & {
  source: 'prop' | 'event';
};

import React, { useState, ChangeEvent, FocusEvent, KeyboardEvent, useMemo } from 'react';
import { Input, Tooltip } from 'antd';
import { InfoCircleOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { NumericFormat } from 'react-number-format';
import { parseLocaleNumber } from 'src/Utils/formatNumber';

interface InputFieldProps {
  value: string | number | null;
  name: string;
  onChange: (e: any, name: string) => void;
  inputType: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
  language?: 'en' | 'de';
  errorMessage?: string;
  testId?: string;
  heightProp?: string;
  labelHeight?: string;
  overallPadding?: string;
  overallMargin?: string;
  borderColor?: string;
  borderNone?: boolean;
  inputTextColor?: string;
  maxCount?: number;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  prefixIcon?: string;
  suffixIcon?: string;
  showLength?: boolean;
  handleClear?: () => void;
  required?: boolean;
  toolTipContent?: string;
  validate?: boolean;
  showErrorMessage?: boolean;
  title?: string;
  infoText?: string;
  decimalScale?: number;
  defaultValue?: string | number | null;
  isZeroRequired?: boolean;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>, name: string) => void;
  onInvalid?: (e: React.InvalidEvent<HTMLInputElement>) => void;
  disableSpecialChar?: boolean;
  isPercentage?: boolean;
  showHint?: boolean;
}

const InputField: React.FC<InputFieldProps> = ({
  value,
  defaultValue,
  name,
  onChange,
  inputType,
  className = '',
  testId = '',
  placeholder,
  disabled = false,
  language = 'en',
  errorMessage = '',
  heightProp = '48px',
  labelHeight = '',
  overallPadding = '0px 16px',
  overallMargin = '',
  borderColor,
  borderNone = false,
  showErrorMessage = true,
  inputTextColor = '#000000',
  maxCount,
  onBlur,
  onFocus,
  prefixIcon,
  suffixIcon,
  showLength,
  handleClear,
  required,
  toolTipContent,
  validate,
  title,
  decimalScale = 2,
  isZeroRequired = true,
  onKeyDown,
  onInvalid,
  disableSpecialChar,
  isPercentage = false,
  showHint = false,
}) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);

  const handleChangeFormatted = (values: { floatValue: number | undefined }, { source }: { source: 'prop' | 'event' }) => {
    if (source === 'prop') return;
    const floatVal = values.floatValue;
    const normalizedValue = floatVal === undefined ? '' : floatVal;

    // Apply percentage limit conditionally
    if (floatVal && isPercentage && floatVal > 100) return;

    if (normalizedValue !== value) {
      const syntheticEvent: SyntheticChangeEvent = {
        target: {
          name,
          value: normalizedValue,
        } as unknown as EventTarget & HTMLInputElement,
        source,
      } as SyntheticChangeEvent;

      onChange(syntheticEvent, name);
    }
  };

  const displayValue = useMemo(() => {
    if (value === 0 || value === '0' || value === '0.0' || value === '0.00') {
      return '';
    }
    if (typeof value === 'number') return value;
    return parseLocaleNumber(value ?? '', language, isZeroRequired);
  }, [isZeroRequired, language, value]);

  const handleToggleInputType = () => {
    setShowPassword(!showPassword);
  };

  const isPasswordStrong = (password: string): boolean => {
    if (password.length < 8) return false;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*]/.test(password);

    if (hasUpper && hasLower && hasNumber && hasSpecial) {
      return true;
    } else {
      return false;
    }
  };

  const getPasswordStrengthColor = (strength: string, errorText: string) => {
    if (disabled) {
      return '#E5E5E5';
    }
    if (!errorText && isHovered && !strength && strength !== 'strong') {
      return '#F4F9FF';
    }
    if (!errorText && isFocused && !strength && strength !== 'strong') {
      return '#FFFFFF';
    }
    if (validate || errorText) {
      if (errorText) {
        return '#FFE0E0';
      }
      switch (strength) {
        case 'weak':
        case 'schwach':
          return '#FEDF89';
        case 'medium':
        case 'mittel':
          return '#FEF0C7';
        case 'strong':
        case 'stark':
          return '#D1FADF';
        default:
          return '#FFFFFF';
      }
    }
  };
  const allowedNumber =
    name?.toLowerCase().includes('contact') ||
    name?.toLowerCase().includes('phone') ||
    name?.toLowerCase().includes('mobile') ||
    name?.toLowerCase().includes('postal_code') ||
    name?.toLowerCase().includes('vat') ||
    name?.toLowerCase().includes('no_of_employees') ||
    name?.toLowerCase().includes('duns') ||
    name?.toLowerCase().includes('comp_related');
  const isZeroValue = value === 0;
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const nextValue = e.target?.value ?? e.currentTarget?.value ?? '';
    const normalizedEvent = {
      ...e,
      target: {
        ...(e.target || {}),
        name,
        value: nextValue,
      },
      currentTarget: {
        ...(e.currentTarget || {}),
        name,
        value: nextValue,
      },
    } as ChangeEvent<HTMLInputElement>;

    // Call the onChange function with the updated value
    onChange(normalizedEvent, name);

    // Handle password strength (if applicable)
    if (inputType === 'password') {
      if (isPasswordStrong(t(nextValue))) {
        setIsPasswordValid(true);
      } else if (isPasswordValid && !isPasswordStrong(t(nextValue))) {
        setIsPasswordValid(false);
      }
    }
  };

  const getPasswordStrengthBorderColor = (strength: string, errorText: string) => {
    if (disabled) {
      return '#A3A3A3';
    }
    if (!errorText && isHovered && !strength && strength !== 'strong') {
      return '#E5E5E5';
    }
    if (!errorText && isFocused && !strength && strength !== 'strong') {
      return '#2961A4';
    }
    if (validate || errorText) {
      if (errorText) {
        return '#D30303';
      }
      switch (strength) {
        case 'weak':
        case 'schwach':
          return '#DC6803';
        case 'medium':
        case 'mittel':
          return '#FEC84B';
        case 'strong':
        case 'stark':
          return '#039855';
        default:
          return '#737373';
      }
    }
  };
  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    if (inputType === 'password') {
      const val = e.target.value;
    }
    if (onBlur) {
      onBlur(e);
    }
  };
  const handleFocus = () => {
    setIsFocused(true);
  };
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if ((inputType === 'number' && e.key === 'e') || (inputType === 'number' && e.key === 'E')) {
      e.preventDefault();
    }
    if (inputType === 'number' && ['-', 'e', '+'].includes(e.key)) {
      e.preventDefault();
    }
    if (disableSpecialChar && inputType === 'number' && e.key === '-') {
      e.preventDefault();
    }
    if (onKeyDown) {
      onKeyDown(e, name);
    }
  };

  const inputValueLength = String(value ?? '').length;
  const resolvedDefaultValue = defaultValue !== null && defaultValue !== undefined
    ? String(defaultValue)
    : '';
  const resolvedValue = value !== null && value !== undefined
    ? String(value)
    : '';

  const renderSuffix = () => {
    const actionSuffix =
      inputType === 'password' ? (
        <Tooltip title={showPassword ? t('Hide Password') : t('Show Password')}>
          {showPassword ? <EyeOutlined onClick={handleToggleInputType} /> : <EyeInvisibleOutlined onClick={handleToggleInputType} />}
        </Tooltip>
      ) : suffixIcon ? (
        <img
          onClick={handleClear}
          src={suffixIcon}
          alt="suffix"
          className="mr-2"
          width={20}
          height={20}
        />
      ) : null;

    return actionSuffix;
  };

  const handleInvalid = (e: React.InvalidEvent<HTMLInputElement>) => {
    const target = e.target as HTMLInputElement | null;
    if (target && typeof target.setCustomValidity === 'function') {
      target.setCustomValidity('');
    }
    onInvalid?.(e);
  };

  const renderInput = () => {
    const inputStyle = {
      borderColor: errorMessage ? '#D30303' : disabled ? !value ? 'none' : '#A3A3A3' : isFocused ? '#2961A4' : isHovered ? '#E5E5E5' : value ? '#737373' : '#737373',
      backgroundColor: errorMessage
        ? '#FEEBEB'
        : (disabled && value) || isZeroValue
          ? '#F1FCFB'
          : disabled && !value
            ? '#E5E5E5'
            : isHovered
              ? '#F4F9FF'
              : value
                ? '#FFFFFF'
                : '#FFFFFF',
      borderRadius: '4px',
      padding: overallPadding,
      margin: overallMargin,
      height: heightProp,
      color: inputTextColor,
      ...(borderNone ? { border: 'none' } : {}),
    };
    const commonInputProps = {
      disabled,
      testId,
      className: `${className} text-base`,
      onBlur,
      onFocus: () => {
        setIsFocused(true);
        onFocus?.();
      },
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
      maxLength: maxCount,
      placeholder: t(placeholder || ''),
    };
    if (inputType === 'number' && !allowedNumber) {
      return (
        <NumericFormat
          value={displayValue !== null && displayValue !== undefined ? displayValue : ''}
          data-testid={testId}
          // fixedDecimalScale={isDecimal}
          isAllowed={(values) => {
            const { floatValue } = values;
            return floatValue === undefined || !isPercentage || floatValue <= 100;
          }}
          onValueChange={handleChangeFormatted}
          thousandSeparator={language === 'de' ? '.' : ','}
          decimalSeparator={language === 'de' ? ',' : '.'}
          decimalScale={decimalScale}
          onKeyDown={handleKeyDown}
          onInvalid={handleInvalid}
          allowNegative={false}
          customInput={Input}
          {...commonInputProps}
          style={inputStyle}
        />
      );
    }

    const type = showPassword ? 'text' : inputType;

    return (
      <div className="relative w-full">
        <Input
          defaultValue={resolvedDefaultValue}
          value={resolvedValue}
          name={name}
          placeholder={t(placeholder || '')}
          onChange={handleInputChange}
          data-testid={testId}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          onInvalid={handleInvalid}
          className={`${className} text-base`}
          type={type}
          prefix={
            prefixIcon ? (
              <img
                src={prefixIcon}
                alt="prefix"
                className="mr-2"
                width={24}
                height={24}
              />
            ) : null
          }
          suffix={renderSuffix()}
          style={{
            borderColor: errorMessage
              ? '#D30303'
              : isHovered
                ? '#E5E5E5'
                : disabled
                  ? '#A3A3A3'
                  : isFocused
                    ? '#2961A4'
                    : borderColor ? (value ? '#525252' : '#525252')
                      : value
                        ? '#737373'
                        : '#737373',
            backgroundColor: errorMessage ? '#FEEBEB' : disabled ? '#E5E5E5' : '#FFFFFF',
            borderRadius: '4px',
            padding: overallPadding,
            paddingRight: showLength && maxCount ? '64px' : undefined,
            margin: overallMargin,
            height: heightProp,
            ...(borderNone ? { border: 'none' } : {}),
            color: inputTextColor,
          }}
          disabled={disabled}
          maxLength={maxCount}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        />
        {showLength && maxCount ? (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#737373] whitespace-nowrap">
            {inputValueLength}/{maxCount}
          </span>
        ) : null}
      </div>
    );
  };

  return (
    <div className={errorMessage ? `mb-0 w-full ${overallMargin}` : `w-full ${overallMargin}`}>
      {title && (
        <div
          className="flex items-center"
          style={{ marginBottom: '0.5vmin' }}
        >
          <span
            className={`pr-2 ${labelHeight}  text-base`}
            style={{
              color: '#000000',
            }}
          >
            {t(title)}
            {required ? <span className="text-[#E40303] text-[16px]">*</span> : <></>}
            {(inputType === 'password' && validate) ||
              (toolTipContent && (
                <Tooltip
                  placement="right"
                  title={
                    inputType === 'password'
                      ? t('passwordValidationHint')
                      : toolTipContent
                  }
                >
                  <InfoCircleOutlined
                    className="text:xs 3xl:text-sm"
                    style={{
                      marginLeft: '8px',
                      color: '#737373',
                    }}
                  />
                </Tooltip>
              ))}
          </span>
        </div>
      )}
      {renderInput()}

      {errorMessage ? (
        <div
          style={{
            minHeight: '1rem',
            color: '#D30303',
            visibility: errorMessage ? 'visible' : 'hidden',
          }}
          className="text-xs 3xl:text-sm mt-1"
          data-testid={`${testId}-error-field`}
        >
          {t(errorMessage || ' ')}
        </div>
      ) : inputType === 'password' && showHint && !isPasswordValid ? (
        <div
          style={{
            minHeight: '1rem',
            color: '#737373',
          }}
          className="text-xs 3xl:text-sm mt-1"
          data-testid="password-hint"
        >
          {t('passwordValidationHint')}
        </div>
      ) : (
        <div
          style={{
            minHeight: '1rem',
            color: '#D30303',
            marginTop: '0.25rem',
            display: !errorMessage && showErrorMessage ? 'block' : 'none',
          }}
          className="text-md md:text-xs lg:text-xs"
        ></div>
      )}
    </div>
  );
};

export default InputField;
